/**
 * Back book radar: the ONE money formatter and the paid-down wording.
 *
 * Every dollar figure on the radar (columns, score breakdown, worklist) goes
 * through formatCentsAud, from integer cents, so two places showing the same
 * amount can never differ by a dollar. The server's reason strings round the
 * same way (half away from zero), but the UI renders paid-down money from the
 * cents fields regardless.
 */

import type { RadarRow } from './types';

/** Integer cents -> whole AUD, rounded half away from zero ("$192,564"). null -> "n/a". */
export function formatCentsAud(cents: number | null): string {
  if (cents === null) return 'n/a';
  return (cents / 100).toLocaleString('en-AU', { style: 'currency', currency: 'AUD', maximumFractionDigits: 0 });
}

const MONTHS = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];

export function formatMonth(ym: string | null): string | null {
  const m = ym ? /^(\d{4})-(\d{2})$/.exec(ym) : null;
  return m ? `${MONTHS[Number(m[2]) - 1]} ${m[1]}` : null;
}

/**
 * Paid-down for the table cell: the amount, and the basis as short secondary
 * text. A lower bound always says "at least"; it is never presented as exact.
 */
export function paidDownParts(row: RadarRow): { amount: string; basis: string | null } {
  if (row.paidDownCents === null) {
    return row.paidDownBasis ? { amount: 'n/a', basis: 'balance increased' } : { amount: 'n/a', basis: null };
  }
  const amount = formatCentsAud(row.paidDownCents);
  switch (row.paidDownBasis) {
    case 'original':
      return { amount, basis: 'since settlement' };
    case 'entered_original':
      return {
        amount,
        basis: row.paidDownEnteredBy ? `since settlement (entered by ${row.paidDownEnteredBy})` : 'since settlement (entered)',
      };
    case 'first_statement_near_settlement':
      return { amount, basis: 'since settlement (first statement)' };
    case 'first_statement_within_12m':
    case 'since_first_statement': {
      const since = formatMonth(row.paidDownSinceMonth);
      return { amount, basis: since ? `at least, since ${since}` : 'at least' };
    }
    default:
      return { amount, basis: null };
  }
}

/** Paid-down as one sentence (the score breakdown line). Money from the cents field. */
export function paidDownLabel(row: RadarRow): string {
  if (row.paidDownCents === null) return row.paidDownBasis ? 'Balance increased' : 'n/a';
  const amount = formatCentsAud(row.paidDownCents);
  switch (row.paidDownBasis) {
    case 'original':
      return `Paid down ${amount} since settlement`;
    case 'entered_original':
      return row.paidDownEnteredBy
        ? `Paid down ${amount} since settlement (original entered by ${row.paidDownEnteredBy})`
        : `Paid down ${amount} since settlement (original entered manually)`;
    case 'first_statement_near_settlement':
      return `Paid down ${amount} since settlement (from first statement)`;
    case 'first_statement_within_12m':
    case 'since_first_statement': {
      const since = formatMonth(row.paidDownSinceMonth);
      return since ? `Paid down at least ${amount} since ${since}` : `Paid down at least ${amount}`;
    }
    default:
      return 'n/a';
  }
}

/** The score breakdown lines: the server's labels, with paid-down re-rendered from cents. */
export function breakdownLines(row: RadarRow): Array<{ label: string; points: number }> {
  return (row.scoreBreakdown ?? []).map((b) => ({
    label: b.kind === 'paid_down' ? paidDownLabel(row) : b.label,
    points: b.points,
  }));
}
